import React, {
  useState,
  useEffect,
  useCallback,
  useContext,
  useRef,
} from 'react';
import { useHistory } from 'react-router';
import qs from 'query-string-for-all';
import useDebounce from 'react-use/lib/useDebounce';
import useClickAway from 'react-use/lib/useClickAway';

import { AppContext } from '@prisjakt/ui-app-context';
import { useViewportContext } from 'src/components/viewport/context';

import { SuggestionBox } from './suggestion-box';
import { SuggestionItems } from './components/items';

import { graphql } from 'src/globals/api/requests';
import suggestionsQuery from './query.graphql';
import {
  addRecentSearch,
  getRecentSearches,
  deleteRecentSearch,
  clearRecentSearches,
} from './history-storage';
import { extractFilter } from './filter-helpers';

import {
  trackSearchSuggestionClicked,
  trackSearchSuggestionsListed,
  trackSearchHistoryListed,
} from './tracking';

import * as S from './styles';

import {
  Suggestion,
  SuggestionType,
  HistoryItem as SuggestedHistory,
  OriginalItem,
  SuggestionTypeEnum,
  ThinProduct,
  SUGGESTION_ORDER,
} from './types';
import { SuggestedProductCollection, PopularSearch } from 'src/types';
import { PopularSearches } from 'src/components/popular-searches';
import popularSearchesQuery from './requests/popular-searches.graphql';
import { useEventListener } from 'src/hooks/use-event-listener';

const NAV_ID_ORIGINAL = 0;

const validTypes: (SuggestionType | 'history' | 'original')[] = Object.values(
  SuggestionTypeEnum
);

type Props = {
  onClose: () => void;
  query: string;
  setQuery: (query: string) => void;
  visible: boolean;
  updatedInputElement: React.RefObject<HTMLInputElement> | null;
  updatedSearchWrapperElement: React.RefObject<HTMLDivElement> | null;
};

type PageTypes =
  | 'product'
  | 'brand'
  | 'product_collection'
  | 'category'
  | 'store'
  | 'vcategory'
  | 'landing_page';

export const gotoUrl = async (
  type: PageTypes,
  id: string,
  queryParam: string,
  meta?: string
) => {
  const params = { q: queryParam, meta };
  const res = await fetch(
    `/_internal/goto/${type}/${id}?${qs.stringify(params)}`
  );

  if (res.status !== 200) {
    throw new Error(`Cannot resolve url for ${type}`);
  }

  return res.text();
};

const createHistorySuggestion: (
  query: string,
  index: number
) => SuggestedHistory = (query, index) => ({
  type: 'history',
  __typename: 'history',
  text: query,
  navId: 100 + index,
});

const createOriginalSuggestion: (q: string) => OriginalItem = (
  query: string
) => ({
  navId: NAV_ID_ORIGINAL,
  text: query,
  type: 'original',
  __typename: 'original',
});

const mapNewTypes = {
  SuggestedProduct: 'product' as 'product',
  SuggestedBrand: 'brand' as 'brand',
  SuggestedProductCollection: 'product_collection' as 'product_collection',
  SuggestedCategory: 'category' as 'category',
  SuggestedShop: 'store' as 'store',
  SuggestedVirtualCategory: 'vcategory' as 'vcategory',
  SuggestedLandingPage: 'landing_page' as 'landing_page',
};

const filterHistory = (history: string, query: string) =>
  history.toLowerCase().indexOf(query?.toLowerCase()) > -1;

const Suggestions = (props: Props) => {
  const {
    query,
    onClose,
    setQuery,
    visible,
    updatedInputElement,
    updatedSearchWrapperElement,
  } = props;

  const { market, t, captureException } = useContext(AppContext);
  const { isMobile } = useViewportContext();
  const [selected, setSelected] = useState(0);

  const [searchInputElement, setSearchInputElement] = useState<
    HTMLInputElement
  >();
  const [searchWrapperElement, setSearchWrapperElement] = useState<
    HTMLDivElement
  >();
  const containerRef = useRef<HTMLUListElement | null>(null);
  const history = useHistory();

  // Keep track of the request count to discard old results
  const requestCount = useRef(0);

  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [products, setProducts] = useState<{ id: number; price: number }[]>([]);
  const [popularSearches, setPopularSearches] = useState<PopularSearch[]>([]);

  // Tracking
  useDebounce(
    () => {
      const suggestionsCount = suggestions.filter(suggestion =>
        validTypes.includes(suggestion.type)
      ).length;
      const historyCount = suggestions.filter(
        suggestion => suggestion.type === 'history'
      ).length;

      if (visible && suggestionsCount)
        trackSearchSuggestionsListed(
          suggestions.map(item => item.type).join(', ')
        );
      if (visible && historyCount) trackSearchHistoryListed();
    },
    500,
    [suggestions, visible]
  );
  useEffect(() => {
    const getPopularSearches = async () => {
      const response = await graphql(popularSearchesQuery);
      setPopularSearches(response?.data ? response.data?.popularSearches : []);
    };
    getPopularSearches();
  }, []);

  useEffect(() => {
    const searchInput = Array.from(
      document.querySelectorAll('[data-suggestions="search"]')
    ).find(
      (el): el is HTMLInputElement =>
        el instanceof HTMLInputElement && el.offsetParent !== null
    );

    if (searchInput) setSearchInputElement(searchInput);

    const searchWrapper = Array.from(
      document.querySelectorAll('[data-suggestions="searchWrapper"]')
    ).find(
      (el): el is HTMLDivElement =>
        el instanceof HTMLDivElement && el.offsetParent !== null
    );

    if (searchWrapper) setSearchWrapperElement(searchWrapper);
  }, [visible]);

  const setCurrentSuggestion = useCallback(
    (suggestion: Suggestion) => {
      const modifiedQuery = `${suggestion.text.trim()} `;
      setQuery(modifiedQuery);

      if (!searchInputElement) return;
      searchInputElement.value = modifiedQuery;
      searchInputElement.dispatchEvent(new CustomEvent('change'));
    },
    [searchInputElement, setQuery]
  );

  const handleClickPopular = (popularQuery: string) => {
    setQuery(popularQuery.trim());
    onClose();
  };

  const handleClickSuggestion = useCallback(
    async (suggestion: Suggestion, index: number) => {
      const { push } = history;

      if (
        (suggestion.type === 'original' || !suggestion.text) &&
        query.length < 2
      ) {
        // Don't redirect to the full page result for queries that are too short or not set. It will just fail.
        return;
      }

      setQuery(suggestion.text.trim());

      // Tracking
      if (validTypes.includes(suggestion.type)) {
        // Reciprocal rank, aka. the postion of the suggestion divied by 1 to calculate the reciprocal rank.
        const reciprocalRank = 1 / (index + 1);
        trackSearchSuggestionClicked(
          suggestion.type,
          suggestion.text,
          query,
          reciprocalRank
        );
      }

      switch (suggestion.__typename) {
        case 'SuggestedVirtualCategory':
        case 'SuggestedCategory':
        case 'SuggestedShop':
        case 'SuggestedProduct': {
          try {
            const url = await gotoUrl(
              mapNewTypes[suggestion.__typename],
              suggestion.id.toString(),
              suggestion.text
            );

            if (!url) return;

            onClose();
            push(url);
            break;
            // eslint-disable-next-line no-empty
          } catch (e) {}
          break;
        }
        case 'SuggestedSearchPhrase':
          onClose();
          addRecentSearch(suggestion.text);
          push(`/search?search=${encodeURIComponent(suggestion.text)}`);
          break;
        case 'SuggestedBrand':
          onClose();
          // Perform a search for the brand name as we don't want to
          // send users to the brand page.
          push(`/search?search=${encodeURIComponent(suggestion.text)}`);
          break;
        case 'SuggestedProductCollection':
          const { path } = extractFilter(suggestion); // eslint-disable-line no-case-declarations

          if (!path && !suggestion?.path) {
            return;
          }

          onClose();
          push(path || suggestion.path || '');
          break;
        case 'SuggestedLandingPage':
          if (!suggestion?.path) {
            return;
          }

          onClose();
          push(suggestion.path || '');
          break;
        default: {
          // Prevent race condition when pressing enter quickly after a search
          // because `query` and `suggestion.text` states are out of sync.
          const currentQuery = suggestion.navId === 0 ? query : suggestion.text;
          onClose();
          addRecentSearch(currentQuery);
          push(`/search?search=${encodeURIComponent(currentQuery)}`);
        }
      }
    },
    [history, onClose, query]
  );

  const handleKeyPress = (e: KeyboardEvent) => {
    if (!containerRef.current) {
      return;
    }

    const key = e.key || e.keyCode;

    const originalSuggestion = createOriginalSuggestion(query);
    const activeSuggestions = [originalSuggestion, ...suggestions]
      // Sort based on `navId` for navigation to work when changing displayed order
      .sort((a, b) => (a.navId > b.navId ? 1 : -1));

    switch (key) {
      case 'Enter':
      case 13: {
        const suggestion =
          activeSuggestions.find(s => s.navId === selected) ||
          originalSuggestion;
        const index = activeSuggestions.indexOf(suggestion!);

        if (suggestion.text.length > 0) {
          handleClickSuggestion(suggestion, index);
          if (searchInputElement) {
            searchInputElement.blur();
          }
          e.stopPropagation();
        }
        break;
      }
      case 'ArrowRight':
      case 39: {
        const suggestion = activeSuggestions.find(
          (s: Suggestion) => s.navId === selected
        );

        if (
          suggestion &&
          searchInputElement &&
          searchInputElement.selectionStart ===
            searchInputElement.selectionEnd &&
          searchInputElement.selectionStart === searchInputElement.value.length
        ) {
          searchInputElement.value = `${suggestion.text} `;

          searchInputElement.dispatchEvent(new CustomEvent('focus'));
        }
        // Make sure search bar updates text
        break;
      }
      case 'ArrowUp':
      case 'ArrowDown':
      case 38:
      case 40: {
        e.preventDefault();
        let nextItem;
        const currentIndex = activeSuggestions.findIndex(
          s => s.navId === selected
        );

        if (key === 40 || key === 'ArrowDown') {
          // ArrowDown
          nextItem =
            activeSuggestions[(currentIndex + 1) % activeSuggestions.length];
        } else {
          // ArrowUp
          const nextIndex =
            currentIndex - 1 === -1
              ? activeSuggestions.length - 1
              : currentIndex - 1;

          nextItem = activeSuggestions[nextIndex];
        }

        if (nextItem) {
          if (searchInputElement) {
            if (nextItem.type === 'SuggestedProductCollection') {
              const { text } = extractFilter(
                nextItem as SuggestedProductCollection
              );
              searchInputElement.value = text
                ? `${text} `
                : `${nextItem.text} `;
            } else {
              searchInputElement.value = `${nextItem.text} `;
            }
          }
          setSelected(nextItem.navId);
        }
        break;
      }
      case 'Escape':
      case 'Esc':
      case 27:
        e.preventDefault();
        break;
      default:
        break;
    }
  };

  const handleExtendSearch = (suggestion: Suggestion) => {
    setCurrentSuggestion(suggestion);
    if (searchInputElement) {
      searchInputElement.focus();
    }
  };

  useEventListener('keydown', handleKeyPress);

  useClickAway(containerRef, (e: Event) => {
    if (isMobile) return;
    if (
      e.target instanceof Node &&
      (searchInputElement === e.target ||
        updatedInputElement?.current === e.target ||
        updatedSearchWrapperElement?.current?.contains(e.target) ||
        searchWrapperElement?.contains(e.target))
    ) {
      // It's the search bar, we don't want to close
      return;
    }

    onClose();
  });

  useEffect(() => {
    // Clear old suggestions when erasing input query
    if (query.length === 0) {
      setSuggestions(getRecentSearches().map(createHistorySuggestion));
    }
  }, [query]);

  useEffect(() => {
    // Reset selected state when query changed
    if (selected !== 0) {
      setSelected(0);
    }
  }, [query]);

  // Fetch the regular price for all products
  // Fetched in client for performance optimisations
  useDebounce(
    async () => {
      // Disable fetching of prices based on feature level
      if (window.FEATURE_TOGGLE >= 1) return;
      const productIds = suggestions
        .filter(s => s.__typename === 'SuggestedProduct')
        .map(p => p.__typename === 'SuggestedProduct' && p.id);

      if (productIds?.length) {
        const res = await graphql(
          `
            query productPrices($ids: [Int!]!) {
              products(ids: $ids, allowMissing: true) {
                id
                priceSummary {
                  regular
                }
              }
            }
          `,
          { ids: productIds }
        );

        if (res?.data?.products?.length) {
          setProducts(
            res.data.products.map((p: ThinProduct) => ({
              id: p.id,
              price: p.priceSummary.regular,
            }))
          );
        }
      }
    },
    100,
    [suggestions]
  );

  useDebounce(
    async () => {
      if (query.length === 0 || !query.trim()) {
        return;
      }

      const myRequestCount = requestCount.current + 1;
      requestCount.current = myRequestCount;

      try {
        const res = await graphql(suggestionsQuery, { query });

        if (myRequestCount !== requestCount.current) {
          // The response we're processing is not from the latest request, discard it
          return;
        }

        if (res.errors?.length) {
          setSuggestions(
            getRecentSearches()
              .filter(s => filterHistory(s, query))
              .map(createHistorySuggestion)
          );

          return;
        }

        if (res.data) {
          setSuggestions([
            ...res.data.searchSuggestions.map(
              (s: Suggestion, index: number) => ({
                ...s,
                type: s.__typename,
                // Make sure navigation in based on the actual order displayed
                navId:
                  (s.__typename ? SUGGESTION_ORDER[s.__typename] * 10 : 10) +
                  index,
              })
            ),
            ...getRecentSearches()
              .filter(s => filterHistory(s, query))
              .map(createHistorySuggestion),
          ]);
        }
      } catch (e) {
        captureException(e);
      }
    },
    100,
    [query, market]
  );

  const handleDeleteHistory = (
    e: React.MouseEvent<HTMLButtonElement>,
    historyQuery: string
  ) => {
    e.stopPropagation();

    deleteRecentSearch(historyQuery);

    setSuggestions(prevState => [
      ...prevState.filter(item => item.__typename !== 'history'),
      ...getRecentSearches()
        .filter(s => filterHistory(s, query))
        .map(createHistorySuggestion),
    ]);
  };

  const handleClearHistory = () => {
    clearRecentSearches();

    setSuggestions(prevState => [
      ...prevState.filter(item => item.__typename !== 'history'),
    ]);
  };

  if (!visible || (!suggestions.length && !popularSearches.length)) {
    return null;
  }

  return (
    <>
      <S.SuggestionOverlay $visible={visible} />
      <SuggestionBox containerRef={containerRef}>
        {popularSearches.length &&
        !suggestions.find(s =>
          Object.values(SuggestionTypeEnum).includes(
            // Check if suggestions includes any actual suggestion results
            // recast because TS won't allow this check to ever be falsy.
            s.__typename as SuggestionTypeEnum
          )
        ) ? (
          <>
            <S.PopularSearchesTitle>
              {t('search', 'search.popular-searches')}
            </S.PopularSearchesTitle>
            <PopularSearches
              popularSearches={popularSearches}
              onClick={handleClickPopular}
            />
          </>
        ) : null}
        <SuggestionItems
          query={query}
          items={suggestions}
          handleClick={handleClickSuggestion}
          selectedId={selected}
          handleClearHistory={handleClearHistory}
          handleDeleteHistory={handleDeleteHistory}
          onExtendSearch={handleExtendSearch}
          products={products}
        />
      </SuggestionBox>
    </>
  );
};

export default Suggestions;
